<template>
  <Popup ref="popup" @close="$emit('close')">
    <LoadingOverlay v-if="isSaving" />

    <article class="emoji-editor">
      <h1>{{ $t('adaptive-test-battle.emoji-editor-popup-heading') }}</h1>

      <PublicUserEmoji ref="userEmoji" :emoji="chosenEmoji['value']" />

      <ErrorMessage v-if="hasError">
        <p>{{ errorMessage }}</p>
      </ErrorMessage>

      <div class="emoji-picker-wrapper">
        <EmojiPicker
          ref="emojiPicker"
          @change="onEmojiChange"
          :emojis="emojis"
        />
      </div>

      <ConfirmChoiceButton @click="saveChanges" :disabled="!canSaveEmoji" />
    </article>
  </Popup>
</template>

<script>
import Popup from '../../ui/Popup'
import EmojiPicker from './EmojiPicker'
import PublicUserEmoji from '../username-display/PublicUserEmoji'

import UserApi from '../../../apis/user'
import LoadingOverlay from '../../LoadingOverlay'
import ErrorMessage from '../../ui/ErrorMessage'
import ConfirmChoiceButton from './ConfirmChoiceButton'

export default {
  name: 'PublicEmojiEditorPopup',
  components: {
    ConfirmChoiceButton,
    ErrorMessage,
    LoadingOverlay,
    PublicUserEmoji,
    EmojiPicker,
    Popup,
  },
  props: {
    emoji: String,
    show: Boolean,
  },
  data() {
    return {
      isSaving: false,
      hasError: false,
      emojis: [],
      chosenEmoji: { value: this.emoji },
    }
  },
  mounted() {
    this.$refs.popup.togglePopup(this.show)
  },
  computed: {
    canSaveEmoji() {
      return (
        !!this.chosenEmoji['unlocked'] &&
        this.emoji != this.chosenEmoji['value']
      )
    },
  },
  methods: {
    saveChanges() {
      if (this.emoji !== this.chosenEmoji['value']) {
        this.isSaving = true

        new UserApi()
          .setPublicUsername(undefined, undefined, this.chosenEmoji['id'])
          .then(() => {
            this.isSaving = false
            this.$emit('emojiSaved', this.chosenEmoji['value'])
          })
          .catch(({ message }) => {
            this.isSaving = false
            this.hasError = true
            this.errorMessage = message
          })
      } else {
        this.$emit('close')
      }
    },
    onEmojiChange(emoji) {
      this.chosenEmoji = emoji
      this.$refs.userEmoji.$el.classList.add('bloop-animation')

      setTimeout(() => {
        this.$refs.userEmoji.$el.classList.remove('bloop-animation')
      }, 150)
    },
  },
  watch: {
    show(show) {
      this.$refs.popup.togglePopup(show)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .popup {
  $stripeLight: $brighterYellowPale;
  $stripeDark: $orangeVeryPale;
  $baseWidth: 1.5em;
  display: flex;
  border-radius: $mainBorderRadius;
  background: repeating-linear-gradient(
    90deg,
    $stripeDark,
    $stripeDark $baseWidth,
    $stripeLight $baseWidth,
    $stripeLight ($baseWidth * 2)
  );
  @media screen and (min-width: $popupBreakpoint) {
    max-height: 80vh;
  }
}

.emoji-editor {
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  border-radius: $mainBorderRadius;

  .emoji-picker-wrapper {
    flex: 1;
    width: 100%;
    overflow: auto;
    margin: 1em 0;
    background-color: $white;
    border: 0.2em solid $violet;
    border-radius: $mainBorderRadius;
  }
}

.emoji {
  --emoji-border-color: #{$brighterYellowPale};
  --emoji-background-color: #{$violet};
  --emoji-border-width: 0.1em;

  font-size: 3em;
  transition: all 0.15s ease-in-out;

  &.bloop-animation {
    transform: scale(1.2);
  }
}

.error-message {
  background-color: $brighterYellowPale;
  border: 0.3em solid $errorText;
  border-radius: $mainBorderRadius;
  padding: 0 0.5em;
}
</style>
